import { bind } from 'bind-event-listener';
import uniqBy from 'lodash/uniqBy';
import { type Environment, fetchQuery, graphql } from 'react-relay';

import type {
	initialMentionAgentsQuery,
	initialMentionAgentsQuery$data,
} from './__generated__/initialMentionAgentsQuery.graphql.ts';
import { mapEdgeToAgent } from './map-edge-to-agent.ts';
import type { MentionAgent } from './types.ts';

const PREFETCH_FAVOURITES_COUNT = 5;
const PREFETCH_VERIFIED_COUNT = 5;
const PREFETCH_ALL_COUNT = 30;

export type InitialMentionAgentsParams = {
	environment: Environment;
	cloudId: string;
	product: string;
	workspaceId?: string;
	signal?: AbortSignal;
};

export type InitialMentionAgentsResult = {
	mergedAgents: readonly MentionAgent[];
	canFilterClientSide: boolean;
};

export const initialMentionAgents = async (
	params: InitialMentionAgentsParams,
): Promise<InitialMentionAgentsResult> => {
	if (params.signal?.aborted) {
		return { mergedAgents: [], canFilterClientSide: false };
	}

	const data = await new Promise<initialMentionAgentsQuery$data | null | undefined>(
		(resolve, reject) => {
			let unbindAbortListener: (() => void) | null = null;
			const settle = (value: initialMentionAgentsQuery$data | null | undefined) => {
				unbindAbortListener?.();
				resolve(value);
			};
			const fail = (error: Error) => {
				unbindAbortListener?.();
				reject(error);
			};

			const subscription = fetchQuery<initialMentionAgentsQuery>(
				params.environment,
				graphql`
					query initialMentionAgentsQuery(
						$cloudId: String!
						$product: String
						$workspaceId: String
						$favouritesFirst: Int!
						$verifiedFirst: Int!
						$allFirst: Int!
					) {
						favourites: agentStudio_getAgents(
							cloudId: $cloudId
							product: $product
							workspaceId: $workspaceId
							first: $favouritesFirst
							input: { onlyFavouriteAgents: true }
						) @optIn(to: "AgentStudio") {
							edges {
								node {
									...mapEdgeToAgent
								}
							}
						}
						verified: agentStudio_getAgents(
							cloudId: $cloudId
							product: $product
							workspaceId: $workspaceId
							first: $verifiedFirst
							input: { onlyVerifiedAgents: true }
						) @optIn(to: "AgentStudio") {
							edges {
								node {
									...mapEdgeToAgent
								}
							}
						}
						all: agentStudio_getAgents(
							cloudId: $cloudId
							product: $product
							workspaceId: $workspaceId
							first: $allFirst
						) @optIn(to: "AgentStudio") {
							pageInfo {
								hasNextPage
							}
							edges {
								node {
									...mapEdgeToAgent
								}
							}
						}
					}
				`,
				{
					cloudId: params.cloudId,
					product: params.product,
					workspaceId: params.workspaceId,
					favouritesFirst: PREFETCH_FAVOURITES_COUNT,
					verifiedFirst: PREFETCH_VERIFIED_COUNT,
					allFirst: PREFETCH_ALL_COUNT,
				},
				{ fetchPolicy: 'store-or-network' },
			).subscribe({
				next: settle,
				error: fail,
			});

			if (params.signal) {
				unbindAbortListener = bind(params.signal, {
					type: 'abort',
					listener: () => {
						subscription.unsubscribe();
						settle(null);
					},
					options: { once: true },
				});
			}
		},
	);

	const collect = (
		bucket: initialMentionAgentsQuery$data['favourites'] | null | undefined,
	): MentionAgent[] => {
		if (!bucket) {
			return [];
		}
		const result: MentionAgent[] = [];
		for (const edge of bucket.edges) {
			const mapped = mapEdgeToAgent(edge?.node);
			if (mapped) {
				result.push(mapped);
			}
		}
		return result;
	};

	const favourites = collect(data?.favourites);
	const verified = collect(data?.verified);
	const all = collect(data?.all);

	const mergedAgents = uniqBy([...favourites, ...verified, ...all], 'id');

	const canFilterClientSide = data?.all != null && !data.all.pageInfo?.hasNextPage;

	return { mergedAgents, canFilterClientSide };
};
